/**
 * The UI primitives (DESIGN_SYSTEM §4).
 *
 * These are written rather than imported from a component library because the design direction is specific
 * and most libraries' defaults fight it: big radii, drop shadows on everything, floating cards. The brief
 * is explicit — hairlines over boxes, no heavy shadows, no big rounded cards, one accent.
 */
import type { ComponentPropsWithoutRef, ReactNode } from 'react';
import { EmptyCan } from './moments';
import {
  AlertTriangle, Check, CheckCircle2, ChevronRight, CircleDashed, Clock, CloudOff, FlaskConical,
  Hourglass, Info, Ruler, SkipForward, XCircle, Ban, CalendarClock, FileCheck2, Home, Lock, MapPin,
  Package, PackageCheck, PlayCircle, QrCode, Scale, Truck, UserCheck, type LucideIcon,
} from 'lucide-react';
import { CARD_HELP } from './helpTexts';
import { dateTime as fmtDateTime } from './format';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

/* ── Section label ───────────────────────────────────────────────────────────────────────────────── */

export function SectionLabel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cx('label-text', className)}>{children}</div>;
}

/* ── Status: tone + icon + word, always all three ────────────────────────────────────────────────── */

export type Tone = 'accepted' | 'queued' | 'refused' | 'info' | 'admin' | 'neutral';

const TONE_CLASS: Record<Tone, string> = {
  accepted: 'text-accepted bg-accepted-ground',
  queued: 'text-queued bg-queued-ground',
  refused: 'text-refused bg-refused-ground',
  info: 'text-info bg-info-ground',
  admin: 'text-admin bg-admin-ground',
  neutral: 'text-neutral bg-neutral-ground',
};

export function StatusBadge({
  tone, icon: Icon, children, title,
}: {
  tone: Tone;
  /** Required: colour is never the only signal (WCAG 1.4.1, and the client's own rule). */
  icon: LucideIcon;
  children: ReactNode;
  title?: string;
}) {
  return (
    <span
      title={title}
      className={cx(
        'inline-flex items-center gap-1 rounded-[var(--radius-badge)] px-1.5 py-0.5',
        'font-mono text-badge font-bold tracking-[0.06em] uppercase whitespace-nowrap',
        TONE_CLASS[tone],
      )}
    >
      <Icon size={11} strokeWidth={2} aria-hidden />
      {children}
    </span>
  );
}

/** The fixed status vocabulary from DESIGN_SYSTEM §5. A status not in here needs a design decision. */
export const STATUS: Record<string, { tone: Tone; icon: LucideIcon; word: string }> = {
  HEALTHY: { tone: 'accepted', icon: CheckCircle2, word: 'Healthy' },
  DELIVERED: { tone: 'accepted', icon: CheckCircle2, word: 'Delivered' },
  PARTIAL: { tone: 'queued', icon: CircleDashed, word: 'Part delivered' },
  FAILED: { tone: 'refused', icon: XCircle, word: 'Not delivered' },
  SKIPPED: { tone: 'neutral', icon: SkipForward, word: 'Skipped' },
  PENDING: { tone: 'queued', icon: Clock, word: 'Pending' },
  UNASSESSED: { tone: 'admin', icon: Ruler, word: 'Unassessed' },
  SYNC_FAILED: { tone: 'refused', icon: CloudOff, word: 'Sync failed' },
  AWAITING_APPROVAL: { tone: 'queued', icon: Hourglass, word: 'Awaiting approval' },
  SIMULATION: { tone: 'admin', icon: FlaskConical, word: 'Simulation' },
  STAGING: { tone: 'admin', icon: FlaskConical, word: 'Staging' },
  NEVER_RUN: { tone: 'neutral', icon: CircleDashed, word: 'Never run' },
  STALE: { tone: 'queued', icon: Clock, word: 'Stale' },
  FAILING: { tone: 'refused', icon: AlertTriangle, word: 'Failing' },
  QUARANTINED: { tone: 'refused', icon: CloudOff, word: 'Quarantined' },
  DISABLED: { tone: 'neutral', icon: CircleDashed, word: 'Disabled' },
  PLANNED: { tone: 'admin', icon: Info, word: 'Planned' },

  // ── EPIC 13 · the round ──────────────────────────────────────────────────────────────────────
  // Every one pairs a tone with an icon and a word, because colour is never the only signal — the
  // operational meanings the brief sets out (planned · attention · done · partial · failed · cancelled)
  // map onto the tones that already exist rather than introducing a second palette.
  DRAFT: { tone: 'neutral', icon: CircleDashed, word: 'Draft' },
  // ── v7 CORE-2 · a customer ───────────────────────────────────────────────────────────────────
  ACTIVE: { tone: 'accepted', icon: CheckCircle2, word: 'Active' },
  STARTING: { tone: 'info', icon: Clock, word: 'Starting' },
  PAUSED: { tone: 'queued', icon: Hourglass, word: 'Paused' },
  INACTIVE: { tone: 'neutral', icon: CircleDashed, word: 'Inactive' },
  CONFIRMED: { tone: 'info', icon: Check, word: 'Confirmed' },
  ASSIGNED: { tone: 'info', icon: UserCheck, word: 'Assigned' },
  LOADED: { tone: 'info', icon: PackageCheck, word: 'Loaded' },
  DEPARTED: { tone: 'info', icon: Truck, word: 'On the road' },
  RETURNED: { tone: 'queued', icon: Home, word: 'Back' },
  RECONCILIATION_REQUIRED: { tone: 'queued', icon: Scale, word: 'To reconcile' },
  CLOSED: { tone: 'accepted', icon: CheckCircle2, word: 'Closed' },
  CANCELLED: { tone: 'neutral', icon: Ban, word: 'Cancelled' },
  IN_PROGRESS: { tone: 'info', icon: PlayCircle, word: 'In progress' },
  RESCHEDULED: { tone: 'queued', icon: CalendarClock, word: 'Moved' },
  ARRIVED: { tone: 'info', icon: MapPin, word: 'At the door' },
  COMPLETED: { tone: 'accepted', icon: CheckCircle2, word: 'Done' },
  // Exceptions.
  OPEN: { tone: 'refused', icon: AlertTriangle, word: 'Open' },
  ACKNOWLEDGED: { tone: 'queued', icon: Hourglass, word: 'Acknowledged' },
  RESOLVED: { tone: 'accepted', icon: CheckCircle2, word: 'Resolved' },
  // The door label.
  SCANNED: { tone: 'accepted', icon: QrCode, word: 'Scanned' },
  BYPASSED: { tone: 'refused', icon: Lock, word: 'Bypassed' },
  MISMATCH: { tone: 'refused', icon: AlertTriangle, word: 'Wrong door' },
  // Container custody.
  WITH_FLEET: { tone: 'neutral', icon: Package, word: 'At plant' },
  IN_TRANSIT: { tone: 'info', icon: Truck, word: 'On the road' },
  WITH_CUSTOMER: { tone: 'info', icon: Home, word: 'With customer' },
  SOLD: { tone: 'accepted', icon: Home, word: 'Sold (one-way jar)' },
  LOST: { tone: 'refused', icon: XCircle, word: 'Lost' },
  RETIRED: { tone: 'neutral', icon: Ban, word: 'Retired' },
  // How a stop's position was decided. Not a status, but it reads in the same column and the weaker
  // basis must be visible rather than implied.
  GPS: { tone: 'accepted', icon: MapPin, word: 'GPS' },
  HINT: { tone: 'queued', icon: Ruler, word: 'No pin' },
  SOURCE: { tone: 'admin', icon: FileCheck2, word: 'From field' },
  MANUAL: { tone: 'admin', icon: UserCheck, word: 'By hand' },
};

export function Status({ code, fallbackWord }: { code: string; fallbackWord?: string }) {
  const entry = STATUS[code];
  if (!entry) {
    // An unknown status is shown verbatim rather than guessed at or hidden.
    return (
      <StatusBadge tone="neutral" icon={Info} title="This status has no design entry yet">
        {fallbackWord ?? code}
      </StatusBadge>
    );
  }
  return (
    <StatusBadge tone={entry.tone} icon={entry.icon}>
      {entry.word}
    </StatusBadge>
  );
}

/* ── Honesty primitives (DESIGN_SYSTEM §1.5) ─────────────────────────────────────────────────────── */

/** Unknown is an em dash, never a zero. A zero is a measurement; this is the absence of one. */
export function Unknown({ title = 'No value' }: { title?: string }) {
  return (
    <span className="text-ink-faint" title={title}>
      —
    </span>
  );
}

/** Marks how a figure came to exist, so a calculated number is never mistaken for a measured one. */
export function Basis({ kind }: { kind: 'measured' | 'calculated' | 'cfg' }) {
  const label = kind === 'cfg' ? 'cfg' : kind;
  const title =
    kind === 'measured'
      ? 'Measured at source'
      : kind === 'calculated'
        ? 'Derived from other figures'
        : 'Depends on configuration the client has not confirmed';
  return (
    <span className="ml-1 font-mono text-[11px] tracking-wide uppercase text-ink-faint align-super" title={title}>
      {label}
    </span>
  );
}

/** Where a record came from. A mirrored or simulated record never looks like an ERP-authored one. */
export function ProvenanceBadge({ origin, syncedAt }: { origin: string; syncedAt?: string | null }) {
  const when = syncedAt ? fmtDateTime(syncedAt) : undefined;
  if (origin === 'SIMULATION') return <Status code="SIMULATION" />;
  if (origin === 'STAGING') return <Status code="STAGING" />;
  return (
    <span className="font-mono text-badge tracking-[0.06em] uppercase text-ink-muted" title={when && `Synced ${when}`}>
      {origin}
    </span>
  );
}

/* ── Buttons ─────────────────────────────────────────────────────────────────────────────────────── */

type ButtonProps = ComponentPropsWithoutRef<'button'> & {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'default' | 'dense';
};

export function Button({ variant = 'secondary', size = 'default', className, ...props }: ButtonProps) {
  return (
    <button
      {...props}
      className={cx(
        'btn inline-flex items-center justify-center gap-1.5 rounded-[var(--radius-control)] border',
        `btn-${variant}`,
        'font-medium transition-colors duration-[120ms] disabled:opacity-45 disabled:cursor-not-allowed',
        size === 'dense' ? 'h-[28px] px-2 text-small' : 'h-[32px] px-3 text-body',
        variant === 'primary' && 'bg-brand text-on-brand border-brand hover:bg-brand-hover',
        variant === 'secondary' && 'bg-surface text-ink border-line-strong hover:bg-surface-sunken',
        variant === 'ghost' && 'bg-transparent text-ink-muted border-transparent hover:bg-surface-sunken hover:text-ink',
        variant === 'danger' && 'bg-surface text-refused border-line-strong hover:bg-refused-ground',
        className,
      )}
    />
  );
}

/* ── Panels: hairline structure; raised only in the glass style (.panel, appearance.css; ADR-0036) ──────────────────────────────────────────────────── */

export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cx('panel rounded-[var(--radius-panel)] border border-line bg-surface', className)}>
      {children}
    </section>
  );
}

/** "?" beside a card title that has a help sentence (v7 Part D pattern 20; the texts are in helpTexts.ts). */
function CardHelpMark({ title }: { title: ReactNode }) {
  const key = typeof title === 'string' ? title.split(' · ')[0]! : '';
  const text = CARD_HELP[key];
  if (!text) return null;
  return (
    <span tabIndex={0} role="img" aria-label={`Help: ${text}`} title={text}
      className="inline-flex h-4 w-4 shrink-0 cursor-help items-center justify-center rounded-full border border-line text-[11px] font-semibold text-ink-faint hover:text-ink">?</span>
  );
}

export function PanelHeader({ title, actions, note }: { title: ReactNode; actions?: ReactNode; note?: ReactNode }) {
  return (
    <header className="flex items-center justify-between gap-4 border-b border-line px-4 py-2.5">
      <div className="min-w-0">
        <h2 className="text-section flex items-center gap-1 truncate">{title}<CardHelpMark title={title} /></h2>
        {note ? <p className="mt-0.5 text-small text-ink-muted">{note}</p> : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </header>
  );
}

/* ── States ──────────────────────────────────────────────────────────────────────────────────────── */

export function EmptyState({ title, detail, action }: { title: string; detail?: ReactNode; action?: ReactNode }) {
  return (
    <div className="empty-state flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <EmptyCan />
      {/* In the glass look the message is written on a torn note (the owner, 2026-10-01). */}
      <p className="empty-note text-section">{title}</p>
      {detail ? <p className="max-w-prose text-small text-ink-muted">{detail}</p> : null}
      {action}
    </div>
  );
}

/** Text from the server in plain words (v7 CORE-1 §4): decision, epic, plan and record references are for developers. */
export function plainText(text: string | null | undefined): string {
  return (text ?? '')
    .replace(/\s*\((?:planned, )?EPIC \d+[^)]*\)/g, ' (later)')
    .replace(/\s*\((?:client decision |owner, )?(?:CD-\d+|ADR-\d+|D\d+ phase [^)]*|v\d+ S\d+[^)]*)(?:[,;][^)]*)?\)/g, '')
    .replace(/\s*\((?:[a-z_]+\.)+[a-z_]+\)/g, '')
    .replace(/\bsomeone with (?:[a-z_]+\.)+[a-z_]+/g, 'an administrator')
    .replace(/\b(?:operations|plant|farm|delivery|sales|quality|inventory|units|config|iam)\.[a-z_]+(?:\.[a-z_]+)*\b/g, 'the setting')
    .replace(/\bclient decision CD-\d+/g, 'the owner’s decision')
    .replace(/\bis CD-\d+\b/g, 'is the owner’s to decide')
    .replace(/\bCD-\d+\b/g, 'an owner decision')
    .replace(/\bEPIC \d+(?: \/ \d+)?\b/g, 'a later release')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/** A refusal in plain words: the leading code and any decision reference are for developers (v7 CORE-1 §4). */
export function plainError(message: string | undefined | null): string {
  return (message ?? '').replace(/^[A-Z][A-Z0-9_]+:\s*/, '').replace(/\s*\((?:client decision )?CD-\d+[^)]*\)/g, '').replace(/client decision CD-\d+/g, 'the owner’s decision').trim();
}

export function ErrorState({ error, retry }: { error: unknown; retry?: () => void }) {
  const err = error as { code?: string; message?: string } | undefined;
  const isUndecided = err?.code === 'RTC_UNDECIDED_POLICY';

  return (
    // `data-error` is how a test knows this is an error rather than a screen that happens to use the word.
    // The browser gate used to grep the page text for "Something went wrong", which matched the delivery
    // exceptions register — whose subtitle begins with exactly that phrase.
    <div data-error={isUndecided ? 'undecided' : 'error'}
         className="flex flex-col items-center justify-center gap-3 px-6 py-14 text-center">
      <StatusBadge tone={isUndecided ? 'admin' : 'refused'} icon={isUndecided ? Ruler : AlertTriangle}>
        {isUndecided ? 'Awaiting a decision' : err?.code === 'RTC_FORBIDDEN' ? 'Not allowed' : 'Could not load'}
      </StatusBadge>
      <p className="max-w-prose text-body">
        {isUndecided
          ? 'This depends on a business rule the client has not decided yet, so the system will not guess it.'
          : plainError(err?.message) || 'Something went wrong.'}
      </p>
      {isUndecided ? <p className="max-w-prose text-small text-ink-muted">{plainError(err?.message)}</p> : null}
      {retry ? (
        <Button onClick={retry} size="dense">
          Try again
        </Button>
      ) : null}
    </div>
  );
}

export function LoadingRows({ rows = 6, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div aria-busy="true" aria-live="polite" className="divide-y divide-line">
      <span className="sr-only">Loading…</span>
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex h-[var(--height-row)] items-center gap-4 px-4">
          {Array.from({ length: columns }).map((__, c) => (
            <div
              key={c}
              className="skeleton h-2 animate-pulse rounded-[2px] bg-surface-sunken"
              style={{ width: `${[18, 12, 9, 14, 7][c % 5]}%` }}
            />
          ))}
        </div>
      ))}
    </div>
  );
}

/* ── Facts ───────────────────────────────────────────────────────────────────────────────────────── */

export function FactGrid({ children, columns = 3 }: { children: ReactNode; columns?: number }) {
  return (
    <dl className="grid gap-x-6 gap-y-3 px-4 py-3" style={{ gridTemplateColumns: `repeat(${columns}, minmax(0,1fr))` }}>
      {children}
    </dl>
  );
}

export function Fact({ label, children, mono }: { label: string; children: ReactNode; mono?: boolean }) {
  return (
    <div className="min-w-0">
      <dt className="label-text">{label}</dt>
      <dd className={cx('mt-0.5 truncate', mono && 'code-text')}>{children}</dd>
    </div>
  );
}

export { ChevronRight, Check };
